import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { DropdownItem } from "../ui/dropdown/DropdownItem";
import { Dropdown } from "../ui/dropdown/Dropdown";
import { clearAuthSession, getCurrentUser } from "../../lib/auth";
import api from "../../lib/api";

function initials(name: string): string {
  return name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

export default function UserDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const user = useMemo(() => getCurrentUser(), []);
  // gestion-staff-my-account: avatarUrl/bio/etc. no viven en la sesión
  // cacheada de SSO (StoredUser no los declara) — se trae fresco aquí en vez
  // de tocar el flujo de login compartido con id.satelab.org.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me").then((res) => {
      if (!cancelled) setAvatarUrl(res.data.data?.avatarUrl ?? null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  function toggleDropdown() {
    setIsOpen(!isOpen);
  }

  function closeDropdown() {
    setIsOpen(false);
  }

  function handleLogout() {
    clearAuthSession();
    closeDropdown();
    // Best-effort: also revoke the shared '.satelab.org' hub session cookie —
    // without this, ProtectedRoute's redirectToSso() would silently sign the
    // user right back in from the still-live hub session on the next visit,
    // making "logout" a no-op. Hard navigation (not react-router's navigate)
    // so ProtectedRoute remounts fresh and re-evaluates isStaffAuthenticated().
    api.post("/auth/logout").catch(() => {});
    window.location.href = "/";
  }

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        className="flex items-center text-gray-700 dropdown-toggle dark:text-gray-400"
      >
        <span className="mr-3 flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-xs font-semibold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          {avatarUrl ? <img src={avatarUrl} alt={user?.name || "Usuario"} className="h-full w-full object-cover" /> : initials(user?.name || "?")}
        </span>

        <span className="block mr-1 font-medium text-theme-sm">{user?.name || "Admin"}</span>
        <svg
          className={`stroke-gray-500 dark:stroke-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          width="18"
          height="20"
          viewBox="0 0 18 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.3125 8.65625L9 13.3437L13.6875 8.65625"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <Dropdown
        isOpen={isOpen}
        onClose={closeDropdown}
        className="absolute right-0 mt-[17px] flex w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark"
      >
        <div>
          <span className="block font-medium text-gray-700 text-theme-sm dark:text-gray-400">
            {user?.name || "Administrador"}
          </span>
          <span className="mt-0.5 block text-theme-xs text-gray-500 dark:text-gray-400">
            {user?.email || "Sin email"}
          </span>
        </div>

        <div className="py-4 mt-4 border-t border-gray-200 dark:border-gray-800">
          <DropdownItem
            onClick={() => navigate("/account")}
            onItemClick={closeDropdown}
            tag="button"
            className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
          >
            Mi cuenta
          </DropdownItem>
          <DropdownItem
            onClick={handleLogout}
            onItemClick={closeDropdown}
            tag="button"
          className="flex items-center gap-3 px-3 py-2 mt-3 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300"
          >
            Cerrar sesion
          </DropdownItem>
        </div>
      </Dropdown>
    </div>
  );
}
